import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './page.module.css';
import AppBar from '../../components/AppBar';
import { useUser } from '../../context/UserContext';
import { useTheme } from '../../context/ThemeProvider';
import ContentGrid from '../../components/ContentGrid';
import { GlobalMicroButton } from '../../components/GlobalMicroButton';
import CardButton from '../../components/CardButton';
import GlobalTabs from '../../components/GlobalTabs';
import AppFooter from '../../components/AppFooter';
import RunningProcessModal from '../../components/RunningProcessModal';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { UserTile } from '../../components/UserTile';
import { CompanyList } from '../../components/CompanyTile';
import { ProjectList } from '../../components/ProjectTile';
import { ProjectModal } from '../../components/ProjectModal';
import { KnowledgeList } from '../../components/KnowledgeTile';
import { KnowledgeModal } from '../../components/KnowledgeModal';
import InvitationModal from '../../components/InvitationModal';
import { CompanyIntroductionModal } from '../../components/CompanyIntroductionModal';
import GlobalGptAccessPanel from '../../components/GlobalGptAccessPanel';
import GlobalSidebar from '../../components/GlobalSidebar';
import NavigationSidebar from '../../components/NavigationSidebar';
import { getLocalizedUserRoleDisplayName } from '../../utils/userRoles';
import { useDocuments } from '../../hooks/useDocuments';
import { useRoleBasedData } from '../../hooks/useRoleBasedData';
import { useCompanies } from '../../hooks/useCompanies';
import { useProjects } from '../../hooks/useProjects';
import { useKnowledge } from '../../hooks/useKnowledge';
import { GlobalPagination } from '../../components/GlobalPagination';
import { FamilyMember } from './types';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FilterBar } from '../../components/FilterBar';
import { useToast } from '../../context/ToastProvider';
import GlobalEmptyState from '../../components/GlobalEmptyState';
import { LANGUAGE_TO_FLAG_MAP, LANGUAGE_NAMES } from '../../utils/languages';
import { DocumentPermissionService, PermissionError } from '../../services/DocumentPermissionService';
import { db } from '../../lib/firebase';
import { buildPagination as buildPaginationController, paginate as paginateController, filterDocuments as filterDocumentsController, filterUsers as filterUsersController } from './controller';
export default function HomePage() {
  const { user, firstName, role, company, loading: userLoading } = useUser();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();
  const { t } = useLocale();
  const { documents: realDocuments } = useDocuments(user);
  const {
    documents: roleBasedDocuments,
    users: roleBasedUsers,
    loadingUsers: loadingRoleBasedUsers,
    refetchUsers: refetchRoleBasedUsers,
    tabLabels
  } = useRoleBasedData();
  const showToast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showCompanyIntroductionModal, setShowCompanyIntroductionModal] = useState(false);
  const [showAiSidebar, setShowAiSidebar] = useState(false);
  const [showNavigationSidebar, setShowNavigationSidebar] = useState(false);
  const [userListRefreshKey, setUserListRefreshKey] = useState(0); // Key to force re-render
  const [isProcessing, setIsProcessing] = useState(false);
  const [animationKey, setAnimationKey] = useState(0); // Key to force re-animation on tab switch
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter] = useState('');
  const [categoryFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState<'all' | 'active' | 'invited' | 'blocked'>('all');
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [, setDocsPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [userLanguage, setUserLanguage] = useState<string>('en');
  const [loadingLanguage, setLoadingLanguage] = useState<boolean>(false);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);
  const [, setSponsorshipBadge] = useState<{ enabled: boolean; text: string; logoUrl: string } | null>(null);
  const [, setCompanyData] = useState<any>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [, setActiveTab] = useState(0);

  // Company-related state
  const { companies, loading: companiesLoading, error: companiesError, refetch: fetchCompanies } = useCompanies();
  const [companySearchQuery, setCompanySearchQuery] = useState('');

  // Project-related state
  const { projects, loading: projectsLoading, refetch: fetchProjects } = useProjects();
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [showProjectModal, setShowProjectModal] = useState(false);

  // Knowledge-related state
  const { knowledge, loading: knowledgeLoading, refetch: fetchKnowledge } = useKnowledge();
  const [knowledgeSearchQuery, setKnowledgeSearchQuery] = useState('');
  const [showKnowledgeModal, setShowKnowledgeModal] = useState(false);

  // Use role-based documents for filtering
  const documentsToUse = role === 'internal_admin' || role === 'client_admin'
    ? roleBasedDocuments
    : realDocuments;

  // Use role-based users for internal_admin and client_admin, family members for others
  const usersToUse = role === 'internal_admin' || role === 'client_admin'
    ? roleBasedUsers
    : familyMembers;

  const filteredUsers = filterUsersController(usersToUse as any[], {
    search: searchQuery,
    status: userStatusFilter,
    role: roleFilter,
  });

  // Filtered documents (computed but not currently displayed)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filteredDocuments = filterDocumentsController(documentsToUse as any[], {
    search: searchQuery,
    status: statusFilter,
    category: categoryFilter,
  });

  // Pagination helpers
  const buildPagination = (totalItems: number, currentPage: number) =>
    buildPaginationController(totalItems, currentPage, itemsPerPage);
  const paginate = <T,>(items: T[], page: number) =>
    paginateController<T>(items, page, itemsPerPage);

  // Fetch family members from database (for family_member and internal_admin)
  const fetchFamilyMembers = useCallback(async () => {
    if (!user?.uid) {
      return;
    }

    // Don't skip based on role - let the useEffect handle role filtering
    setLoadingUsers(true);
    try {
      // First, get the current user's document to access their familyMembers array
      const currentUserRef = doc(db, 'users', user.uid);
      const currentUserDoc = await getDoc(currentUserRef);

      if (!currentUserDoc.exists()) {
        setFamilyMembers([]);
        return;
      }

      const currentUserData = currentUserDoc.data();
      let familyMemberIds = currentUserData.familyMembers || [];

      // If familyMembers field doesn't exist, initialize it as an empty array
      if (!currentUserData.hasOwnProperty('familyMembers')) {
        await updateDoc(currentUserRef, {
          familyMembers: []
        });
        familyMemberIds = [];
      }

      if (familyMemberIds.length === 0) {
        setFamilyMembers([]);
        return;
      }

      // Firestore 'in' supports up to 30 values – query in chunks and merge
      const chunkArray = <T,>(arr: T[], size: number): T[][] => {
        const chunks: T[][] = [];
        for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
        return chunks;
      };

      const familyMembersRef = collection(db, 'users');
      const idChunks = chunkArray<string>(familyMemberIds, 30);
      const snapshots = await Promise.all(
        idChunks.map(ids => getDocs(query(familyMembersRef, where('__name__', 'in', ids))))
      );
      const familyMembersData: FamilyMember[] = [];

      snapshots.forEach((snapshot) => {
        snapshot.forEach((doc) => {
          const memberData = doc.data();

          // Construct name from firstName and lastName, fallback to name field
          const fullName = memberData.firstName && memberData.lastName
            ? `${memberData.firstName} ${memberData.lastName}`
            : memberData.name || 'Unknown User';

          familyMembersData.push({
            id: doc.id,
            name: fullName,
            type: memberData.type || [1],
            status: memberData.status || 'invited',
            createdAt: memberData.createdAt || 0,
            avatar: memberData.avatar || undefined,
            email: memberData.email || undefined,
            project: memberData.project || {},
            ...memberData
          });
        });
      });

      // Sort by creation date (newest first)
      familyMembersData.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      setFamilyMembers(familyMembersData);
    } catch (error) {
      console.error('❌ Error fetching family members:', error);
      setFamilyMembers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [user?.uid]);

  // Fetch users when component mounts or refresh key changes
  useEffect(() => {

    if (role === 'family_member' || role === 'internal_admin') {
      fetchFamilyMembers();
    }
  }, [userListRefreshKey, user?.uid, fetchFamilyMembers, role]);

  // Additional effect to trigger family members fetch when role becomes available
  useEffect(() => {

    if (user?.uid && (role === 'family_member' || role === 'internal_admin')) {
      fetchFamilyMembers();
    }
  }, [role, user?.uid, fetchFamilyMembers]);

  // Fetch user language from database
  const fetchUserLanguage = useCallback(async () => {
    if (!user?.uid) return;

    setLoadingLanguage(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const language = userData.language || 'en';

        setUserLanguage(language);
      } else {
        setUserLanguage('en'); // Default to English on error
      }
    } catch (error) {
      console.error('❌ Error fetching user language:', error);
      setUserLanguage('en'); // Default to English on error
    } finally {
      setLoadingLanguage(false);
    }
  }, [user?.uid]);

  // Fetch user language when component mounts
  useEffect(() => {
    fetchUserLanguage();
  }, [user?.uid, fetchUserLanguage]);

  // Fetch sponsorship badge from user's company
  const fetchSponsorshipBadge = useCallback(async (userCompanyId: string) => {
    try {
      const companyRef = doc(db, 'companies', userCompanyId);
      const companyDoc = await getDoc(companyRef);

      if (companyDoc.exists()) {
        const companyData = companyDoc.data();
        setCompanyData(companyData); // Store company data for registration code

        const sponsorshipSettings = companyData?.settings?.sponsorshipBadge;
        if (sponsorshipSettings) {
          setSponsorshipBadge({
            enabled: sponsorshipSettings.enabled || false,
            text: sponsorshipSettings.text || '',
            logoUrl: sponsorshipSettings.logoUrl || ''
          });
        }
      }
    } catch (error) {
      console.error('❌ Error fetching sponsorship badge:', error);
    }
  }, []);

  // Fetch sponsorship badge when company changes
  useEffect(() => {
    if (company) {
      fetchSponsorshipBadge(company);
    }
  }, [company, fetchSponsorshipBadge]);

  // Handle viewport width changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Refresh user language when returning from settings
  useEffect(() => {
    const handleFocus = () => {
      fetchUserLanguage();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.uid, fetchUserLanguage]);


  // Invoice upload handlers
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.uid) {
      // Reset file input on error
      if (event.target) {
        event.target.value = '';
      }
      return;
    }

    // Validate file exists and has content
    if (file.size === 0) {
      showToast(t('invoice_upload_error_file_type'), 'error');
      if (event.target) {
        event.target.value = '';
      }
      return;
    }

    // Validate file type - be more lenient for mobile devices
    // Mobile devices may not always provide correct MIME types
    const allowedMimeTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.heif'];
    
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidMimeType = file.type && allowedMimeTypes.includes(file.type.toLowerCase());
    const isValidExtension = fileExtension && allowedExtensions.includes(fileExtension);
    
    // Accept file if either MIME type or extension is valid (mobile-friendly)
    if (!isValidMimeType && !isValidExtension) {
      console.error('Invalid file type:', { 
        name: file.name, 
        type: file.type, 
        extension: fileExtension,
        size: file.size 
      });
      showToast(t('invoice_upload_error_file_type'), 'error');
      if (event.target) {
        event.target.value = '';
      }
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showToast(t('invoice_upload_error_file_size'), 'error');
      if (event.target) {
        event.target.value = '';
      }
      return;
    }

    if (!user || !company) {
      showToast(t('invoice_processing_not_available'), 'error');
      return;
    }

    try {
      // Reset states
      setUploadSuccess(false);
      setUploadProgress(0);
      setIsProcessing(true);

      // Verify file is still accessible (important for mobile devices)
      if (!file || file.size === 0) {
        throw new Error('File is not accessible or empty');
      }

      // Normalize file name for mobile devices - ensure it has proper extension
      let normalizedFileName = file.name;
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      // If file doesn't have extension or has invalid extension, add one based on MIME type
      if (!fileExtension || fileExtension === '.' || !allowedExtensions.includes(fileExtension)) {
        // Determine extension from MIME type
        if (file.type) {
          if (file.type.includes('pdf')) {
            normalizedFileName = file.name.replace(/\.[^.]*$/, '') + '.pdf';
          } else if (file.type.includes('jpeg') || file.type.includes('jpg')) {
            normalizedFileName = file.name.replace(/\.[^.]*$/, '') + '.jpg';
          } else if (file.type.includes('png')) {
            normalizedFileName = file.name.replace(/\.[^.]*$/, '') + '.png';
          } else if (file.type.includes('heic')) {
            normalizedFileName = file.name.replace(/\.[^.]*$/, '') + '.heic';
          } else if (file.type.includes('heif')) {
            normalizedFileName = file.name.replace(/\.[^.]*$/, '') + '.heif';
          } else {
            // Default to .jpg for images without clear type
            normalizedFileName = (file.name.replace(/\.[^.]*$/, '') || 'image') + '.jpg';
          }
        } else {
          // No MIME type, default to .jpg for images
          normalizedFileName = (file.name.replace(/\.[^.]*$/, '') || 'image') + '.jpg';
        }
      }
      
      // Ensure file name is not empty
      if (!normalizedFileName || normalizedFileName.trim() === '') {
        normalizedFileName = `invoice_${Date.now()}.jpg`;
      }

      // Upload file to Firebase Storage
      const storage = getStorage();
      const fileName = `invoices/${user.uid}/${Date.now()}_${normalizedFileName}`;
      const storageRef = ref(storage, fileName);

      console.log('Uploading file:', { 
        name: file.name, 
        type: file.type, 
        size: file.size 
      });

      const uploadTask = uploadBytes(storageRef, file);
      await uploadTask;

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      setUploadProgress(50);

      // Determine if file is an image (not PDF) - backend needs to know this
      const isImageFile = !file.type?.includes('pdf') && !normalizedFileName.toLowerCase().endsWith('.pdf');
      const fileMimeType = file.type || (isImageFile ? 'image/jpeg' : 'application/pdf');
      
      // Call the cloud function
      const response = await fetch('https://europe-west3-thescon.cloudfunctions.net/injectInvoiceViaWeb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileUrl: downloadURL,
          fileName: normalizedFileName,
          fileType: fileMimeType,
          isImage: isImageFile,
          userId: user.uid,
          language: userLanguage
        })
      });

      if (!response.ok) {
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch (e) {
          const text = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, body: ${text}`);
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      let result;
      try {
        const responseText = await response.text();
        console.log('Raw response text:', responseText);
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error('Failed to parse response from server');
      }
      
      // Log the response for debugging
      console.log('Invoice processing response:', JSON.stringify(result, null, 2));

      // Check if we have a valid documentId (non-empty string)
      const documentId = result?.documentId;
      const hasValidDocumentId = documentId && typeof documentId === 'string' && documentId.trim() !== '';
      
      if (hasValidDocumentId) {
        // Successfully processed - document was created
        console.log('✅ Invoice processed successfully, documentId:', documentId);
        setUploadProgress(100);
        setUploadSuccess(true);

        // Wait a moment to show success, then hide modal
        setTimeout(() => {
          setIsProcessing(false);
          setUploadSuccess(false);
          // Refresh the documents list
          // This will be handled by the useDocuments hook automatically
        }, 2000);
      } else if (result?.error) {
        // Error from backend
        console.error('❌ Backend error:', result.error);
        throw new Error(result.error);
      } else if (result?.message) {
        // OCR completed but AI processing is disabled - no document created
        console.log('⚠️ OCR completed but AI processing disabled:', result.message);
        setIsProcessing(false);
        setUploadProgress(0);
        showToast(result.message, 'info');
      } else {
        // Unexpected response format - log everything
        console.error('❌ Unexpected response format:', {
          result,
          hasDocumentId: !!documentId,
          documentIdType: typeof documentId,
          documentIdValue: documentId,
          resultKeys: result ? Object.keys(result) : 'result is null/undefined'
        });
        throw new Error('Invalid response from invoice processing service. No document was created.');
      }

    } catch (error) {
      console.error('Invoice upload error:', error);

      // Handle errors with user-friendly messages
      if (error instanceof Error) {
        const errorMessage = error.message;
        if (errorMessage === PermissionError.COMPANY_NOT_FOUND || errorMessage === PermissionError.USER_NOT_LINKED_TO_COMPANY) {
          showToast(DocumentPermissionService.getErrorMessage(errorMessage), 'error');
        } else {
          // Log more details for debugging
          if (error && typeof error === 'object' && 'code' in error) {
            console.error('Error code:', error.code);
            console.error('Error details:', error);
          }
          showToast(error.message || t('invoice_upload_error_processing'), 'error');
        }
      } else {
        showToast(t('invoice_upload_error_processing'), 'error');
      }
      
      setIsProcessing(false);
    }

    // Reset file input
    if (event.target) {
      event.target.value = '';
    }
  };

  const setProcessingComplete = () => {
    setIsProcessing(false);
    setUploadSuccess(false);
    setUploadProgress(0);
  };

  const handleAddFamilyMember = () => {
    setShowInvitationModal(true);
  };

  const handleAddCompany = () => {
    setShowCompanyIntroductionModal(true);
  };

  const handleInviteUser = () => {
    setShowInvitationModal(true);
  };

  const handleUserAdded = () => {
    // Refresh role-based user list (internal_admin / client_admin see this list)
    if (refetchRoleBasedUsers) refetchRoleBasedUsers();
    // Also bump key so family members list refetches when role is family_member
    setUserListRefreshKey(prev => prev + 1);
  };

  const handleCompanyAdded = () => {
    // Trigger a refresh of the companies list by updating the key
    setUserListRefreshKey(prev => prev + 1);
    // Also refresh companies immediately using the hook's refetch function
    fetchCompanies();
  };

  const handleTabChange = useCallback((newTabIndex: number) => {
    setActiveTab(newTabIndex);
    // Trigger re-animation by updating the key
    setAnimationKey(prev => prev + 1);
  }, []);

  // Auth reports ready before Firestore delivers the user doc; keep Thescon loading until role exists.
  if (userLoading || (user && !role)) {
    return (
      <div className={styles.loadingShell}>
        <AppBar />
        <div className={styles.loadingMain}>
          <img src={isDarkMode ? "/assets/graphics/thescon-loading-white.svg" : "/assets/graphics/thescon-loading.svg"} alt="Loading" width={120} height={120} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>

      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid
        rows="64px 196px auto 48px"
        tabletRows="64px 160px auto 48px"
        mobileRows="32px 48px auto auto 48px"
      >

        {/* Toolbar left side */}
        <div className={`${styles.homeToolbarLeft} ${role === 'internal_admin' ? styles.homeToolbarLeftInternalAdmin : ''}`}>
          {role === 'internal_admin' && (
            <div className={styles.navigationButton}>
              <GlobalMicroButton
                title={t('navigation_menu') || 'Navigation'}
                icon={<img src={isDarkMode ? '/assets/icons/list.triangle.svg' : '/assets/icons/list.triangle.black.svg'} alt="Menu" width={16} height={16} />}
                onClick={() => setShowNavigationSidebar(true)}
              />
            </div>
          )}
          <b>{t('home_welcome_back')}, <span className={styles.email}>{firstName || user?.email || '...'}</span> 👋</b>
        </div>

        {/* Toolbar right side */}
        <div className={styles.homeToolbarRight}>

          {/* Role badge */}
          <div className={styles.roleBadge}>
            <img src="/assets/icons/apple.intelligence.green.svg" alt="Badge" width={16} height={16} />
            <span>{getLocalizedUserRoleDisplayName(role || 'client_admin', t)}</span>
          </div>

          {/* Language button */}
          <GlobalMicroButton
            title={`Language: ${loadingLanguage ? 'Loading...' : (LANGUAGE_NAMES[userLanguage] || userLanguage.toUpperCase())}`}
            icon={
              loadingLanguage ? (
                <img
                  src={isDarkMode ? "/assets/icons/fade.stagger.circles.animation.svg" : "/assets/icons/fade.stagger.circles.animation.black.svg"}
                  alt="Loading"
                  width={16}
                  height={16}
                  className={styles.loadingSpinner}
                />
              ) : (
                <img
                  src={`https://cdn.ipregistry.co/flags/emojitwo/${LANGUAGE_TO_FLAG_MAP[userLanguage] || 'gb'}.svg`}
                  alt="Language Icon"
                  width={16}
                  height={16}
                />
              )
            }
            onClick={() => navigate('/settings?tab=languages')}
          >
            {loadingLanguage ? '...' : (LANGUAGE_NAMES[userLanguage] || userLanguage.toUpperCase())}
          </GlobalMicroButton>

          {/* Settings button */}
          <GlobalMicroButton
            icon={<img src={isDarkMode ? "/assets/icons/person.svg" : "/assets/icons/person.black.svg"} alt="Settings Icon" width={16} height={16} />}
            onClick={() => navigate('/settings')}
          >
            {t('settings_view_title')}
          </GlobalMicroButton>
        </div>

        {/* Home buttons - hidden for client_admin */}
        {role !== 'client_admin' && (
          <div className={styles.homeButtonWrapper}>

            {/* Add project button */}
            {role === 'internal_admin' && (
              <CardButton
                headline={t('home_add_project_title')}
                paragraph={t('home_add_project_subtitle')}
                ctaLabel={t('home_add_project_action')}
                icon={<img src={isDarkMode ? "/assets/graphics/add-project-icon-dark.svg" : "/assets/graphics/add-project-icon.svg"} alt="Add Project" width={32} height={32} />}
                ctaIcon={<img src={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"} alt="Add" width={16} height={16} />}
                ctaIconLeft
                onClick={() => setShowProjectModal(true)}
                disabled={false}
              />
            )}

            {/* Add invoice button */}
            {role === 'family_member' && (
              <CardButton
                headline={t('home_add_invoice_title')}
                paragraph={t('home_add_invoice_subtitle')}
                ctaLabel={t('home_add_invoice_action')}
                icon={<img src={isDarkMode ? "/assets/graphics/add.invoice.graphic.white.svg" : "/assets/graphics/add.invoice.graphic.black.svg"} alt="Add" width={40} height={40} />}
                ctaIcon={<img src={isDarkMode ? "/assets/icons/icloud.and.arrow.up.svg" : "/assets/icons/icloud.and.arrow.up.black.svg"} alt="Upload" width={16} height={16} />}
                ctaIconLeft
                onClick={handleUploadClick}
                disabled={false}
              />
            )}

            {/* Invite user button (for employees to invite others) */}
            {role === 'family_member' && (
              <CardButton
                headline={t('home_invite_user_title')}
                paragraph={t('home_invite_user_subtitle')}
                ctaLabel={t('home_invite_user_action')}
                icon={<img src={isDarkMode ? "/assets/graphics/invite.user.graphic.white.svg" : "/assets/graphics/invite.user.graphic.black.svg"} alt="Invite" width={40} height={40} />}
                ctaIcon={<img src={isDarkMode ? "/assets/icons/paperplane.svg" : "/assets/icons/paperplane.black.svg"} alt="Invite" width={16} height={16} />}
                ctaIconLeft
                onClick={handleAddFamilyMember}
                disabled={false}
              />
            )}

            {/* Add company button */}
            {role === 'internal_admin' && (
              <CardButton
                headline={t('home_add_company_title')}
                paragraph={t('home_add_company_subtitle')}
                ctaLabel={t('home_add_company_action')}
                icon={<img src={isDarkMode ? "/assets/graphics/add-company-icon-dark.svg" : "/assets/graphics/add-company-icon.svg"} alt="Add Company" width={32} height={32} />}
                ctaIcon={<img src={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"} alt="Add" width={16} height={16} />}
                ctaIconLeft
                onClick={handleAddCompany}
                disabled={false}
              />
            )}

            {/* Invite user button */}
            {role === 'internal_admin' && (
              <CardButton
                headline={t('home_invite_user_title')}
                paragraph={t('home_invite_user_subtitle')}
                ctaLabel={t('home_invite_user_action')}
                icon={<img src={isDarkMode ? "/assets/graphics/invite-user-icon-dark.svg" : "/assets/graphics/invite-user-icon.svg"} alt="Invite User" width={32} height={32} />}
                ctaIcon={<img src={isDarkMode ? "/assets/icons/paperplane.svg" : "/assets/icons/paperplane.black.svg"} alt="Add" width={16} height={16} />}
                ctaIconLeft
                onClick={handleInviteUser}
                disabled={false}
              />
            )}
          </div>
        )}

        {/* Tabs */}
        <div className={role === 'client_admin' ? styles.clientAdminTabs : styles.homeTabs}>
          <GlobalTabs
            onChange={handleTabChange}
            queryParam="tab"
            tabs={[
              // Projects tab - only visible for internal_admin (MOVED FIRST)
              ...(role === 'internal_admin' ? [{
                label: `${t('home_tab_projects')} (${projects.length})`,
                queryValue: 'projects',
                content: (
                  <div key="projects-tab">
                    <ProjectList
                      projects={projects}
                      companies={companies}
                      loading={projectsLoading}
                      searchValue={projectSearchQuery}
                      onSearchChange={setProjectSearchQuery}
                      onAddProject={() => setShowProjectModal(true)}
                    />
                  </div>
                ),
              }] : []),
              // Company tab - only visible for internal_admin
              ...(role === 'internal_admin' ? [{
                label: `${t('home_tab_companies')} (${companies.length})`,
                queryValue: 'companies',
                content: (
                  <div key="companies-tab">
                    {companiesError ? (
                      <div className={styles.errorState}>
                        <p className={styles.errorText}>{companiesError}</p>
                        <button
                          onClick={fetchCompanies}
                          className={styles.retryButton}
                        >
                          {t('home_retry')}
                        </button>
                      </div>
                    ) : (
                      <CompanyList
                        companies={companies}
                        loading={companiesLoading}
                        searchValue={companySearchQuery}
                        onSearchChange={setCompanySearchQuery}
                        onAddCompany={handleAddCompany}
                      />
                    )}
                  </div>
                ),
              }] : []),
              // Knowledge tab - only visible for internal_admin
              ...(role === 'internal_admin' ? [{
                label: `${t('home_tab_knowledge')} (${knowledge.length})`,
                queryValue: 'knowledge',
                content: (
                  <div key="knowledge-tab">
                    <KnowledgeList
                      knowledge={knowledge}
                      loading={knowledgeLoading}
                      searchValue={knowledgeSearchQuery}
                      onSearchChange={setKnowledgeSearchQuery}
                      onAddKnowledge={() => setShowKnowledgeModal(true)}
                    />
                  </div>
                ),
              }] : []),
              {
                label: `${role === 'internal_admin' ? t('home_tab_users') : tabLabels.users} (${filteredUsers.length})`,
                queryValue: 'users',
                content: (
                  <div key="users-tab" className={styles.familyList}>
                    {/* FilterBar for internal_admin and client_admin */}
                    {(role === 'client_admin' || role === 'internal_admin') && (
                      <FilterBar
                        filters={[
                          {
                            id: 'status',
                            label: t('home_filter_status'),
                            options: [
                              { value: 'all', label: t('home_filter_all') },
                              { value: 'active', label: t('home_filter_active') },
                              { value: 'invited', label: t('home_filter_invited') },
                              { value: 'blocked', label: t('home_filter_blocked') }
                            ],
                            value: userStatusFilter,
                            onChange: (value) => setUserStatusFilter(value as any)
                          },
                          // Role filter only for internal_admin
                          ...(role === 'internal_admin'
                            ? [
                              {
                                id: 'role',
                                label: t('company_page_role_filter'),
                                options: [
                                  { value: '', label: t('home_filter_all') },
                                  { value: 'internal_admin', label: t('user_role_internal_admin') },
                                  { value: 'client_admin', label: t('user_role_client_admin') },
                                  { value: 'family_member', label: t('user_role_employee') }
                                ],
                                value: roleFilter,
                                onChange: setRoleFilter
                              }
                            ]
                            : [])
                        ]}
                        searchValue={searchQuery}
                        onSearchChange={setSearchQuery}
                        searchPlaceholder={t('home_search_users_placeholder')}
                        showInviteButton={true}
                        onAddUser={handleInviteUser}
                      />
                    )}

                    {(loadingUsers || loadingRoleBasedUsers) ? (
                      <div className={styles.loadingContainer}>
                        <p>{t('home_loading_users')}</p>
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <GlobalEmptyState />
                    ) : (
                      <div className={styles.familyMembersContainer} style={{ marginTop: role === 'client_admin' || role === 'internal_admin' ? '16px' : '0' }}>
                        {/* Users/Family members */}
                        {paginate(filteredUsers, usersPage).map((userItem: any) => (
                          <div key={`${userItem.id}-${animationKey}`} className={styles.familyMemberItem}>
                            <UserTile
                              user={userItem}
                              disabled={role === 'family_member'}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ marginTop: '12px' }}>
                      <GlobalPagination
                        pagination={buildPagination(filteredUsers.length, usersPage)}
                        actions={{
                          goToPage: setUsersPage,
                          nextPage: () => setUsersPage(p => p + 1),
                          previousPage: () => setUsersPage(p => p - 1),
                          firstPage: () => setUsersPage(1),
                          lastPage: () => setUsersPage(Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage)))
                        }}
                        showItemsPerPage={true}
                        itemsPerPageOptions={[6, 12, 24, 48]}
                        onItemsPerPageChange={(n) => { setItemsPerPage(n); setDocsPage(1); setUsersPage(1); }}
                      />
                    </div>
                  </div>
                ),
              },
              // Fourth tab - Thescon AI (only visible for internal_admin and below 1024px)
              ...(role === 'internal_admin' && isMobileView ? [{
                label: t('home_tab_morgen_ai'),
                queryValue: 'ai',
                content: (
                  <div key="ai-tab" className={styles.morgenAiTab}>
                    <GlobalGptAccessPanel hideBorderTop />
                  </div>
                ),
              }] : []),
            ]}
          />
        </div>


        {/* Separator */}
        <div className={styles.gridSeparatorWrapper}>
          <div className={styles.gridSeparator} />
        </div>

        {/* Side section - Thescon AI (click anywhere → /thescon-ai for now) */}
        {role === 'internal_admin' && (
          <div className={styles.sideSection}>
            <GlobalGptAccessPanel companyId='OKGCziNDxHTksvd9Iei5'/>
          </div>
        )}

        {/* Hidden upload input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          accept="image/*,application/pdf,.pdf,.jpg,.jpeg,.png,.heic,.heif"
        />

        {/* Footer */}
        <AppFooter inline />

      </ContentGrid>

      {/* Running process modal */}
      <RunningProcessModal
        isVisible={isProcessing}
        onComplete={() => setProcessingComplete()}
        messages={[
          t('processing_sheet_animated_text_01'),
          t('processing_sheet_animated_text_02'),
          t('processing_sheet_animated_text_03'),
          t('processing_sheet_animated_text_04'),
          t('processing_sheet_animated_text_05'),
          t('processing_sheet_animated_text_06')
        ]}
        progress={uploadProgress}
        showSuccess={uploadSuccess}
      />

      {/* Invitation modal */}
      <InvitationModal
        isOpen={showInvitationModal}
        onClose={() => setShowInvitationModal(false)}
        userId={user?.uid || ''}
        onUserAdded={handleUserAdded}
        showRoleSelection={role === 'internal_admin'}
      />

      {/* Company introduction modal */}
      <CompanyIntroductionModal
        isOpen={showCompanyIntroductionModal}
        onClose={() => setShowCompanyIntroductionModal(false)}
        onCompanyAdded={handleCompanyAdded}
      />

      {/* Project modal */}
      <ProjectModal
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        onProjectAdded={() => {
          fetchProjects();
          setProjectSearchQuery('');
        }}
      />

      {/* Knowledge modal */}
      <KnowledgeModal
        isOpen={showKnowledgeModal}
        onClose={() => setShowKnowledgeModal(false)}
        onKnowledgeAdded={() => {
          fetchKnowledge();
          setKnowledgeSearchQuery('');
        }}
      />

      {/* AI Expert Sidebar */}
      <GlobalSidebar
        isOpen={showAiSidebar}
        onClose={() => setShowAiSidebar(false)}
      >
        <div>

          {/* Header */}
          <div className={styles.aiExpertSidebarHeader}>
            <img src={isDarkMode ? "/assets/identity/logo-dark.svg" : "/assets/identity/logo.svg"} alt="Thescon" width={16} height={16} />
            <span>|</span>
            <h2>AI</h2>
          </div>

          {/* Content */}
          <GlobalGptAccessPanel hideBorderTop />
        </div>
      </GlobalSidebar>

      {/* Navigation Sidebar */}
      {role === 'internal_admin' && (
        <NavigationSidebar
          isOpen={showNavigationSidebar}
          onClose={() => setShowNavigationSidebar(false)}
        />
      )}
    </div>
  );
}